import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Drawer } from '../../components/ui/Drawer';
import { deletedRowClass } from '../../utils/deletedRow';

interface RepairIssueRow {
  recno: number;
  repairtype: string;
  issue: string;
  repaircategoryrecno: number | null;
  category_name: string | null;
  deleted: string;
}

interface CategoryOption {
  recno: number;
  name: string;
  repairtype: string;
}

interface IssueFormData {
  issue: string;
  repaircategoryrecno: number | null;
  deleted: string;
}

const DEFAULT_FORM: IssueFormData = {
  issue: '',
  repaircategoryrecno: null,
  deleted: '',
};

const REPAIR_TYPE_STYLES: Record<string, string> = {
  Mechanical: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  Cosmetic: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
  Preventative: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
};

const DrawerErrorBanner: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
    <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-red-700 dark:text-red-300 flex-1">{message}</p>
    <button onClick={onDismiss} className="text-red-400 hover:text-red-600 dark:hover:text-red-200 flex-shrink-0">
      <span className="sr-only">Dismiss</span>
      <XCircleIcon className="w-4 h-4" />
    </button>
  </div>
);

const IssueForm: React.FC<{
  formData: IssueFormData;
  onChange: (data: IssueFormData) => void;
  categories: CategoryOption[];
  disabled?: boolean;
  error?: string | null;
  onDismissError?: () => void;
  isEditing?: boolean;
}> = ({ formData, onChange, categories, disabled, error, onDismissError, isEditing }) => {
  return (
    <div className="space-y-6">
      {error && onDismissError && <DrawerErrorBanner message={error} onDismiss={onDismissError} />}

      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Issue Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.issue}
          onChange={(e) => onChange({ ...formData, issue: e.target.value })}
          placeholder="e.g., Flat Tire, Brake Pad Worn"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          disabled={disabled}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.repaircategoryrecno || ''}
          onChange={(e) =>
            onChange({
              ...formData,
              repaircategoryrecno: e.target.value ? parseInt(e.target.value) : null,
            })
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          disabled={disabled}
        >
          <option value="">Select a category</option>
          {categories.map((c) => (
            <option key={c.recno} value={c.recno}>
              {c.name} ({c.repairtype})
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted">
          The repair type is determined by the selected category.
        </p>
      </div>

      {isEditing && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formData.deleted === 'Y'}
              onChange={(e) => onChange({ ...formData, deleted: e.target.checked ? 'Y' : 'N' })}
              disabled={disabled}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-label">Deleted</span>
          </label>
          <p className="mt-1 ml-7 text-xs text-muted">
            Soft-deletes this record — hidden from the list but preserved. Uncheck and save to restore.
          </p>
        </div>
      )}
    </div>
  );
};

const RepairIssues: React.FC = () => {
  const [data, setData] = useState<RepairIssueRow[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Edit Drawer
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<RepairIssueRow | null>(null);
  const [editFormData, setEditFormData] = useState<IssueFormData>(DEFAULT_FORM);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editDrawerError, setEditDrawerError] = useState<string | null>(null);

  // Add Drawer
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [formData, setFormData] = useState<IssueFormData>(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [addDrawerError, setAddDrawerError] = useState<string | null>(null);

  const handleOpenEditDrawer = useCallback((row: RepairIssueRow) => {
    setEditingRow(row);
    setEditFormData({
      issue: row.issue,
      repaircategoryrecno: row.repaircategoryrecno,
      deleted: row.deleted,
    });
    setEditDrawerError(null);
    setIsEditDrawerOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;
    if (!editFormData.issue?.trim()) {
      setEditDrawerError('Issue name is required.');
      return;
    }
    if (!editFormData.repaircategoryrecno) {
      setEditDrawerError('Category is required.');
      return;
    }

    setIsSavingEdit(true);
    setEditDrawerError(null);

    try {
      const response = await api.put(`/repair-lookups/issues/${editingRow.recno}`, editFormData);
      setData((prev) =>
        prev.map((item) => (item.recno === editingRow.recno ? response.data.data : item))
      );
      setSuccessNotification({
        title: 'Updated Successfully',
        message: `Issue '${editFormData.issue}' has been updated.`,
      });
      closeEditDrawer();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setEditDrawerError(msg);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingRow, editFormData]);

  const columns = useMemo<ColumnDef<RepairIssueRow>[]>(
    () => [
      {
        accessorKey: 'issue',
        header: 'Issue Name',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 250,
        cell: ({ row }) => (
          <span className="font-semibold text-blue-600 dark:text-blue-400">{row.original.issue}</span>
        ),
      },
      {
        accessorKey: 'repairtype',
        header: 'Repair Type',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        cell: ({ row }) => {
          const style = REPAIR_TYPE_STYLES[row.original.repairtype] || '';
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
              {row.original.repairtype}
            </span>
          );
        },
      },
      {
        accessorKey: 'category_name',
        header: 'Category',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 180,
        cell: ({ row }) => {
          const name = row.original.category_name;
          if (!name) return <span className="text-gray-400 dark:text-gray-500 text-sm">Unassigned</span>;
          return <span className="text-sm text-label">{name}</span>;
        },
      },
      {
        accessorKey: 'deleted',
        header: 'Deleted',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: ({ row }) => {
          const del = row.original.deleted === 'Y';
          return (<span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${del ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{del ? 'Yes' : 'No'}</span>);
        },
      },
    ],
    []
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [issuesRes, categoriesRes] = await Promise.all([
        api.get('/repair-lookups/issues', { params: { include_deleted: includeDeleted } }),
        api.get('/repair-lookups/categories'),
      ]);
      setData(issuesRes.data.data || []);
      setCategories(
        (categoriesRes.data.data || []).map((c: any) => ({
          recno: c.recno,
          name: c.name,
          repairtype: c.repairtype,
        }))
      );
    } catch {
      setErrorNotification({
        title: 'Failed to Load Data',
        message: 'Could not load repair issues from the server.',
      });
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddNew = async () => {
    if (!formData.issue?.trim()) {
      setAddDrawerError('Issue name is required.');
      return;
    }
    if (!formData.repaircategoryrecno) {
      setAddDrawerError('Category is required.');
      return;
    }

    setIsSaving(true);
    setAddDrawerError(null);

    try {
      const response = await api.post('/repair-lookups/issues', formData);
      setData((prev) => [...prev, response.data.data]);
      setSuccessNotification({
        title: 'Added Successfully',
        message: `Issue '${formData.issue}' has been created.`,
      });
      closeAddDrawer();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setAddDrawerError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const closeAddDrawer = () => {
    setIsAddDrawerOpen(false);
    setFormData(DEFAULT_FORM);
    setAddDrawerError(null);
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditingRow(null);
    setEditFormData(DEFAULT_FORM);
    setEditDrawerError(null);
  };

  return (
    <div className="h-full w-full p-10 pb-24">
      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="repair-issues-table"
        fieldName="repair-issues-table"
        searchPlaceholder="Search repair issues..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Repair Issues"
        onEditFilters={() => setIsAddDrawerOpen(true)}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add Issue"
        showDeletedToggle={true}
        showDeleted={includeDeleted}
        onShowDeletedChange={setIncludeDeleted}
        getRowClassName={(row, m) => deletedRowClass(row.deleted === 'Y', m.zebra)}
        onRowClick={handleOpenEditDrawer}
      />

      {/* Add Drawer */}
      <Drawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        title="Add New Repair Issue"
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeAddDrawer}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNew}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Add Issue
                </>
              )}
            </button>
          </div>
        }
      >
        <IssueForm
          formData={formData}
          onChange={setFormData}
          categories={categories}
          disabled={isSaving}
          error={addDrawerError}
          onDismissError={() => setAddDrawerError(null)}
        />
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        title="Edit Repair Issue"
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeEditDrawer}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSavingEdit ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <IssueForm
          formData={editFormData}
          onChange={setEditFormData}
          categories={categories}
          disabled={isSavingEdit}
          error={editDrawerError}
          onDismissError={() => setEditDrawerError(null)}
          isEditing
        />
      </Drawer>

      {errorNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>,
        document.body
      )}

      {successNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>,
        document.body
      )}
    </div>
  );
};

export default RepairIssues;
